'use client';

/**
 * Explorer card registry — `key` (from matrix) → React component.
 *
 * Single source of truth for "what does each explorer card render?"
 * Mirrors `INSIGHTS_CARD_REGISTRY` exactly — same primitive, third
 * surface. Adding an explorer card means: one matrix entry on the
 * backend (`EXPLORER_CARDS` in `total_matrix.py`) + one entry here.
 *
 * Modes opt into cards-everywhere explorer rendering by declaring
 * `explorer_cards=[...]` on their `@mode` decoration. The shell's
 * Explorer dispatcher checks the active mode's `explorer_cards`
 * matrix list; non-empty → renders `<ExplorerCardStack />`; empty →
 * falls back to legacy `EXPLORER_COMPONENTS` strategy lookup.
 *
 * @see klyde_edge/ui_config/total_matrix.py — EXPLORER_CARDS
 * @see klyde_edge/ui_config/docs/right_rail_and_canvas_ux_direction.md
 *
 * @layer presentation
 * @arch_component_type registry
 * @arch_domain explorer
 */

import { IntentComposerCard } from './intent/composer-card';
import { IntentAddScheduleCard } from './intent/add-schedule-card';
import { IntentSchedulesCard } from './intent/schedules-card';
import { IntentHistoryCard } from './intent/history-card';
import { VendorListCard } from './vendors/list-card';
import { FavouritesExplorerCard } from './vendors/favourites-explorer-card';
import { CustomProductsExplorerCard } from './vendors/custom-products-explorer-card';
import { LensCard } from './lens-card';
import { RfqFinderCard } from './obeya/rfq-finder-card';
// Surface-agnostic product cards composed by the catalogue lens's Filter
// lens — registered here so the lens resolves them by content_key.
import { ProductSearchInputCard } from '@/components/canvas/cards/products/search-input-card';
import { ProductFacetsCard } from '@/components/canvas/cards/products/facets-card';
import { ObeyaProjectListCard } from './obeya/project-list-card';
import { VendorPortalRfqListCard } from './seller/vendor-portal-rfq-list-card';
// System Obeya (mode9) — one explorer card per available report
// (cards-everywhere extends to the explorer surface).
import { SysObeyaExplorerModuleSummaryCard } from './system_obeya/module-summary-card';
import { SysObeyaExplorerHighCcFunctionsCard } from './system_obeya/high-cc-functions-card';
import { SysObeyaExplorerCohesionCard } from './system_obeya/cohesion-card';
// Analytics (mode9 sel:coverage) — capability-coverage report-picker.
import { AnalyticsExplorerCoverageCard } from './analytics/coverage-tile';
// Investor Relations (mode10) — one data-driven tree card over content_set.
import { ContentTreeCard } from './investor_relations/content-tree-card';
// Product Search (mode11) — category-first browse tree.
import { CategoryTreeCard } from './search/category-tree-card';
import { PricingBenchmarksExplorerCard } from './search/pricing-benchmarks-explorer-card';

type ExplorerCardComponent = React.ComponentType;

export const EXPLORER_CARD_REGISTRY: Record<string, ExplorerCardComponent> = {
  // Intent (mode3) — four cards splitting the legacy
  // `EXPLORER_SCHEMA["intent"]` schema-driven paint into discrete
  // cards. Each wraps the existing `intent-*-section.tsx` component
  // unchanged (cohabitation: consume, don't modify).
  intent_composer: IntentComposerCard,
  intent_add_schedule: IntentAddScheduleCard,
  intent_schedules: IntentSchedulesCard,
  intent_history: IntentHistoryCard,
  // Vendors (mode4) — single card today (flat vendor roster + Add
  // Vendor affordance). Mode4-native: doesn't import VendorsExplorer.
  vendor_list: VendorListCard,
  favourites_explorer: FavouritesExplorerCard,
  // Custom Products (mode11) — WANTS sibling of favourites; the buyer's
  // bespoke wishes, lifted from vendor_list's source='buyer' pseudo-vendor.
  custom_products_explorer: CustomProductsExplorerCard,
  // Products (mode4) — the catalogue lens: Browse↔Filter toggle that
  // mounts the vendor tree OR the search+facets. Composes existing
  // relocatable cards; the left-rail scoping lens.
  // Generic lens primitive (Browse↔Filter); mode4 mounts it as
  // catalogue_lens. Any mode registers a lens content_key + default_props
  // → same component, its own card-lists. Reusable for mode1 etc.
  catalogue_lens: LensCard,
  product_search: ProductSearchInputCard,
  product_facets: ProductFacetsCard,
  // mode1 lens (same primitive) + its RFQ finder.
  workspace_lens: LensCard,
  rfq_finder: RfqFinderCard,
  // Obeya (mode1) — single card with internal Project → (RFQ / PO /
  // Cost Plan / Wish) tree. Mode1-native copy of the legacy
  // ProjectsExplorer; legacy keeps its file for non-mode1 surfaces.
  obeya_project_list: ObeyaProjectListCard,
  // Vendor portal (mode5) — seller's RFQ inbox. Phase 1 thin shim
  // re-exporting `<VendorPortalExplorer />`; mode5-native rewrite
  // lands as a follow-up. Card spec is the contract.
  vendor_portal_rfq_list: VendorPortalRfqListCard,
  // System Obeya (mode9) — one explorer card per available report.
  // Each tile dispatches setExplorerSelection on click and routes
  // the canvas to the matching mode9_<selection> preset. Add a new
  // card here when a new mode9 selection lands.
  sys_obeya_explorer_module_summary: SysObeyaExplorerModuleSummaryCard,
  sys_obeya_explorer_high_cc_functions: SysObeyaExplorerHighCcFunctionsCard,
  sys_obeya_explorer_cohesion: SysObeyaExplorerCohesionCard,
  analytics_explorer_coverage: AnalyticsExplorerCoverageCard,
  // Investor Relations (mode10) — one data-driven tree card (domain →
  // topic → question) over the curated content_set. Vendor_list shape.
  content_tree: ContentTreeCard,
  // Product Search (mode11) — same LensCard primitive; the search_lens
  // card spec's default_props swap the Browse lens to the category tree
  // (vendor demotes to a facet chip in Filter).
  search_lens: LensCard,
  category_tree: CategoryTreeCard,
  // Static peer row above the category tree — click sets sel:benchmarks.
  pricing_benchmarks_explorer: PricingBenchmarksExplorerCard,
  // Future explorer cards drop in here as more modes migrate. Each
  // card is a stand-alone component; data fetches + selection-
  // awareness live inside the card, not in this registry.
};
